import { useEffect, useState } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { storesApi, Store } from '../services/api';

interface StoreFormData {
  store_number: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  district: string;
  market: string;
  region: string;
}

const emptyForm: StoreFormData = {
  store_number: '',
  name: '',
  address: '',
  city: '',
  state: 'FL',
  zip: '',
  phone: '',
  district: '',
  market: '',
  region: '',
};

export default function Stores() {
  const [stores, setStores] = useState<Store[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [formData, setFormData] = useState<StoreFormData>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const storesData = await storesApi.list();
      setStores(storesData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stores');
    } finally {
      setIsLoading(false);
    }
  }

  function openCreateModal() {
    setEditingStore(null);
    setFormData(emptyForm);
    setError(null);
    setShowModal(true);
  }

  function openEditModal(store: Store) {
    setEditingStore(store);
    setFormData({
      store_number: store.store_number,
      name: store.name,
      address: store.address,
      city: store.city,
      state: store.state,
      zip: store.zip,
      phone: store.phone,
      district: store.district,
      market: store.market,
      region: store.region,
    });
    setError(null);
    setShowModal(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      if (editingStore) {
        await storesApi.update(editingStore.id, formData);
      } else {
        await storesApi.create(formData);
      }
      await loadData();
      setShowModal(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save store');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDelete(store: Store) {
    if (!confirm(`Are you sure you want to delete ${store.name}?`)) {
      return;
    }

    try {
      await storesApi.delete(store.id);
      await loadData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete store');
    }
  }

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const getScoreColor = (score: number | undefined) => {
    if (score === undefined) return 'text-gray-400';
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number | undefined) => {
    if (score === undefined) return 'bg-gray-100';
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stores</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage store locations and view visit metrics
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Add Store
        </button>
      </div>

      {/* Stores Table */}
      <div className="bg-white shadow-sm rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th rowSpan={2} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-bottom">
                  Store
                </th>
                <th rowSpan={2} className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider align-bottom">
                  Score
                </th>
                <th colSpan={7} className="px-1 py-1 text-center text-xs font-medium text-gray-400 uppercase tracking-wider border-b border-gray-200">
                  Last Visit
                </th>
                <th rowSpan={2} className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider align-bottom">
                  Cases
                </th>
                <th rowSpan={2} className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider align-bottom">

                </th>
              </tr>
              <tr>
                <th className="px-1 py-1 text-center text-[10px] font-medium text-gray-500 uppercase" title="Store Manager">
                  SM
                </th>
                <th className="px-1 py-1 text-center text-[10px] font-medium text-gray-500 uppercase" title="District Manager">
                  DM
                </th>
                <th className="px-1 py-1 text-center text-[10px] font-medium text-gray-500 uppercase" title="Market Manager">
                  MM
                </th>
                <th className="px-1 py-1 text-center text-[10px] font-medium text-gray-500 uppercase" title="Loss Prevention">
                  LP
                </th>
                <th className="px-1 py-1 text-center text-[10px] font-medium text-gray-500 uppercase" title="Buyer">
                  BYR
                </th>
                <th className="px-1 py-1 text-center text-[10px] font-medium text-gray-500 uppercase" title="Executive Leadership">
                  ELT
                </th>
                <th className="px-1 py-1 text-center text-[10px] font-medium text-gray-500 uppercase" title="Visual Merchandising">
                  VM
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stores.map((store) => (
                <tr key={store.id} className="hover:bg-gray-50">
                  {/* Store Info */}
                  <td className="px-3 py-3">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {store.name}
                      </div>
                      <div className="text-[10px] text-gray-500">
                        #{store.store_number} &middot; {store.district}
                      </div>
                    </div>
                  </td>

                  {/* Overall Score */}
                  <td className="px-2 py-3 text-center">
                    {store.latest_score ? (
                      <div>
                        <div className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${getScoreBgColor(store.latest_score.score)} ${getScoreColor(store.latest_score.score)}`}>
                          {Math.round(store.latest_score.score)}%
                        </div>
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {formatDate(store.latest_score.as_of)}
                        </div>
                      </div>
                    ) : (
                      <span className="text-[10px] text-gray-400">-</span>
                    )}
                  </td>

                  {/* SM Last Visit */}
                  <td className="px-1 py-3 text-center">
                    {store.last_visits?.SM ? (
                      <div>
                        <div className="text-[10px] font-medium text-gray-700">
                          {formatDate(store.last_visits.SM.date)}
                        </div>
                        <div className={`text-[10px] ${getScoreColor(store.last_visits.SM.score)}`}>
                          {Math.round(store.last_visits.SM.score)}%
                        </div>
                      </div>
                    ) : (
                      <span className="text-[10px] text-gray-300">-</span>
                    )}
                  </td>

                  {/* DM Last Visit */}
                  <td className="px-1 py-3 text-center">
                    {store.last_visits?.DM ? (
                      <div>
                        <div className="text-[10px] font-medium text-gray-700">
                          {formatDate(store.last_visits.DM.date)}
                        </div>
                        <div className={`text-[10px] ${getScoreColor(store.last_visits.DM.score)}`}>
                          {Math.round(store.last_visits.DM.score)}%
                        </div>
                      </div>
                    ) : (
                      <span className="text-[10px] text-gray-300">-</span>
                    )}
                  </td>

                  {/* MM Last Visit */}
                  <td className="px-1 py-3 text-center">
                    {store.last_visits?.MM ? (
                      <div>
                        <div className="text-[10px] font-medium text-gray-700">
                          {formatDate(store.last_visits.MM.date)}
                        </div>
                        <div className={`text-[10px] ${getScoreColor(store.last_visits.MM.score)}`}>
                          {Math.round(store.last_visits.MM.score)}%
                        </div>
                      </div>
                    ) : (
                      <span className="text-[10px] text-gray-300">-</span>
                    )}
                  </td>

                  {/* LP Last Visit */}
                  <td className="px-1 py-3 text-center">
                    {store.last_visits?.LP ? (
                      <div>
                        <div className="text-[10px] font-medium text-gray-700">
                          {formatDate(store.last_visits.LP.date)}
                        </div>
                        <div className={`text-[10px] ${getScoreColor(store.last_visits.LP.score)}`}>
                          {Math.round(store.last_visits.LP.score)}%
                        </div>
                      </div>
                    ) : (
                      <span className="text-[10px] text-gray-300">-</span>
                    )}
                  </td>

                  {/* BUYER Last Visit */}
                  <td className="px-1 py-3 text-center">
                    {store.last_visits?.BUYER ? (
                      <div>
                        <div className="text-[10px] font-medium text-gray-700">
                          {formatDate(store.last_visits.BUYER.date)}
                        </div>
                        <div className={`text-[10px] ${getScoreColor(store.last_visits.BUYER.score)}`}>
                          {Math.round(store.last_visits.BUYER.score)}%
                        </div>
                      </div>
                    ) : (
                      <span className="text-[10px] text-gray-300">-</span>
                    )}
                  </td>

                  {/* ELT Last Visit */}
                  <td className="px-1 py-3 text-center">
                    {store.last_visits?.ELT ? (
                      <div>
                        <div className="text-[10px] font-medium text-gray-700">
                          {formatDate(store.last_visits.ELT.date)}
                        </div>
                        <div className={`text-[10px] ${getScoreColor(store.last_visits.ELT.score)}`}>
                          {Math.round(store.last_visits.ELT.score)}%
                        </div>
                      </div>
                    ) : (
                      <span className="text-[10px] text-gray-300">-</span>
                    )}
                  </td>

                  {/* VM Last Visit */}
                  <td className="px-1 py-3 text-center">
                    {store.last_visits?.VM ? (
                      <div>
                        <div className="text-[10px] font-medium text-gray-700">
                          {formatDate(store.last_visits.VM.date)}
                        </div>
                        <div className={`text-[10px] ${getScoreColor(store.last_visits.VM.score)}`}>
                          {Math.round(store.last_visits.VM.score)}%
                        </div>
                      </div>
                    ) : (
                      <span className="text-[10px] text-gray-300">-</span>
                    )}
                  </td>

                  {/* Open Cases */}
                  <td className="px-2 py-3">
                    {store.open_cases && store.open_cases.total > 0 ? (
                      <div className="flex items-center justify-center gap-0.5 flex-wrap">
                        {store.open_cases.critical > 0 && (
                          <span className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-red-100 text-red-800" title="Critical">
                            C:{store.open_cases.critical}
                          </span>
                        )}
                        {store.open_cases.high > 0 && (
                          <span className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-orange-100 text-orange-800" title="High">
                            H:{store.open_cases.high}
                          </span>
                        )}
                        {store.open_cases.medium > 0 && (
                          <span className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-yellow-100 text-yellow-800" title="Medium">
                            M:{store.open_cases.medium}
                          </span>
                        )}
                        {store.open_cases.low > 0 && (
                          <span className="inline-flex items-center px-1 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-800" title="Low">
                            L:{store.open_cases.low}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="text-center">
                        <span className="text-[10px] text-gray-300">-</span>
                      </div>
                    )}
                  </td>

                  {/* Actions */}
                  <td className="px-2 py-3 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => openEditModal(store)}
                      className="text-primary-600 hover:text-primary-900 mr-2"
                      title="Edit store"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(store)}
                      className="text-red-600 hover:text-red-900"
                      title="Delete store"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-4 flex items-center gap-4 flex-wrap text-[10px] text-gray-500">
        <span><strong>SM</strong>=Store Mgr</span>
        <span><strong>DM</strong>=District Mgr</span>
        <span><strong>MM</strong>=Market Mgr</span>
        <span><strong>LP</strong>=Loss Prevention</span>
        <span><strong>BYR</strong>=Buyer</span>
        <span><strong>ELT</strong>=Executive</span>
        <span><strong>VM</strong>=Visual Merch</span>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black bg-opacity-25" onClick={() => setShowModal(false)} />
            <div className="relative bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {editingStore ? 'Edit Store' : 'Add Store'}
              </h2>

              {error && (
                <div className="mb-4 p-3 bg-red-50 rounded-lg text-sm text-red-700">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Store Number
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.store_number}
                      onChange={(e) => setFormData({ ...formData, store_number: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Store Name
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                    <input
                      type="text"
                      required
                      maxLength={2}
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value.toUpperCase() })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ZIP
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.zip}
                      onChange={(e) => setFormData({ ...formData, zip: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      District
                    </label>
                    <input
                      type="text"
                      value={formData.district}
                      onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Market
                    </label>
                    <input
                      type="text"
                      value={formData.market}
                      onChange={(e) => setFormData({ ...formData, market: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Region
                    </label>
                    <input
                      type="text"
                      value={formData.region}
                      onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : editingStore ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
